import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PALETTE, matte, colored, vary, seededRandom, jitter } from '../../Style.js';

const POTION_LIFT = 0.35; // Keeps the flask clear of the plinth at the bottom of its bob

// A healing potion: a corked glass flask of glowing red liquid, bobbing and
// turning above a little mossy plinth, with a soft halo and drifting sparkles.
export class HealthPack {
  constructor(scene, healthPack) {
    this.scene = scene;
    this.healthPack = healthPack;
    this.mesh = null;
    this.potion = null;
    this.sparkles = [];
    this.ownMaterials = [];
    this.bobOffset = Math.random() * Math.PI * 2; // Random starting phase for bobbing
    this.create();
  }

  create() {
    // Create a group to hold all parts
    this.mesh = new THREE.Group();
    const rand = seededRandom(Math.floor(this.bobOffset * 1e6));
    const groundY = -this.healthPack.position.y;

    // Plinth and a glowing ring on the ground marking the pickup
    const plinth = new THREE.Mesh(
      mergeGeometries([
        colored(jitter(new THREE.CylinderGeometry(0.62, 0.8, 0.3, 7), 0.08, rand), vary(PALETTE.stone, rand, 0.1), { y: groundY + 0.15 }),
        colored(jitter(new THREE.CylinderGeometry(0.66, 0.66, 0.06, 7), 0.05, rand), vary(PALETTE.mossStone, rand), { y: groundY + 0.31 })
      ]),
      matte(0xffffff, { vertexColors: true })
    );
    plinth.castShadow = true;
    plinth.receiveShadow = true;
    this.mesh.add(plinth);

    const ringMaterial = this.own(new THREE.MeshBasicMaterial({
      color: 0xff5c86,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
      side: THREE.DoubleSide
    }));
    this.groundRing = new THREE.Mesh(new THREE.RingGeometry(0.95, 1.2, 20), ringMaterial);
    this.groundRing.rotation.x = -Math.PI / 2;
    this.groundRing.position.y = groundY + 0.03;
    this.mesh.add(this.groundRing);

    // The flask, which bobs and turns
    this.potion = new THREE.Group();
    this.potion.position.y = POTION_LIFT;
    this.mesh.add(this.potion);

    // Glowing liquid
    this.liquidMaterial = this.own(new THREE.MeshStandardMaterial({
      color: 0xd8284f,
      emissive: 0xff3366,
      emissiveIntensity: 0.9,
      roughness: 0.6,
      flatShading: true
    }));
    const liquid = new THREE.Mesh(new THREE.SphereGeometry(0.6, 8, 6), this.liquidMaterial);
    liquid.position.y = -0.05;
    this.potion.add(liquid);

    // Glass body, neck and lip
    const glassMaterial = this.own(new THREE.MeshStandardMaterial({
      color: 0xdff2f0,
      roughness: 0.6,
      flatShading: true,
      transparent: true,
      opacity: 0.3,
      depthWrite: false
    }));
    const glass = new THREE.Mesh(
      mergeGeometries([
        new THREE.SphereGeometry(0.75, 8, 6).toNonIndexed(),
        new THREE.CylinderGeometry(0.2, 0.26, 0.55, 8).translate(0, 0.85, 0).toNonIndexed(),
        new THREE.TorusGeometry(0.22, 0.06, 4, 8).rotateX(Math.PI / 2).translate(0, 1.12, 0).toNonIndexed()
      ]),
      glassMaterial
    );
    glass.renderOrder = 1;
    this.potion.add(glass);

    // Cork, twine and a parchment tag
    const fittings = new THREE.Mesh(
      mergeGeometries([
        colored(new THREE.CylinderGeometry(0.18, 0.14, 0.32, 6), PALETTE.woodLight, { y: 1.24 }),
        colored(new THREE.CylinderGeometry(0.235, 0.235, 0.08, 8), PALETTE.hay, { y: 0.95 }),
        colored(new THREE.BoxGeometry(0.02, 0.3, 0.02), PALETTE.hay, { x: 0.24, y: 0.8, z: 0.05, rz: 0.2 }),
        colored(new THREE.BoxGeometry(0.22, 0.28, 0.03), PALETTE.cloth, { x: 0.28, y: 0.58, z: 0.08, rz: 0.15 })
      ]),
      matte(0xffffff, { vertexColors: true })
    );
    fittings.castShadow = true;
    this.potion.add(fittings);

    // Soft halo around the flask
    this.haloMaterial = this.own(new THREE.MeshBasicMaterial({
      color: 0xff4f7a,
      transparent: true,
      opacity: 0.16,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.BackSide
    }));
    this.halo = new THREE.Mesh(new THREE.SphereGeometry(1.05, 10, 8), this.haloMaterial);
    this.halo.position.y = POTION_LIFT + 0.1;
    this.mesh.add(this.halo);

    // Sparkles drifting around it
    const sparkleMaterial = this.own(new THREE.MeshBasicMaterial({ color: 0xffd0dc }));
    const sparkleGeometry = new THREE.OctahedronGeometry(0.07, 0);
    for (let i = 0; i < 3; i++) {
      const sparkle = new THREE.Mesh(sparkleGeometry, sparkleMaterial);
      sparkle.userData.phase = (i / 3) * Math.PI * 2;
      this.sparkles.push(sparkle);
      this.mesh.add(sparkle);
    }

    // Set initial position
    this.mesh.position.set(
      this.healthPack.position.x,
      this.healthPack.position.y,
      this.healthPack.position.z
    );

    this.scene.add(this.mesh);
  }

  own(material) {
    this.ownMaterials.push(material);
    return material;
  }

  // Update animation (bobbing, turning and glowing)
  update(time) {
    if (!this.potion) return;
    const t = time + this.bobOffset;

    // Gentle bobbing motion
    const bob = Math.sin(time * 2 + this.bobOffset) * 0.3;
    this.potion.position.y = POTION_LIFT + bob;

    // Slow rotation with a slight sway
    this.potion.rotation.y = time * 0.5;
    this.potion.rotation.z = Math.sin(t * 1.3) * 0.08;

    // Breathing glow
    const glow = Math.sin(t * 3) * 0.5 + 0.5;
    this.liquidMaterial.emissiveIntensity = 0.75 + glow * 0.35;
    this.halo.position.y = POTION_LIFT + 0.1 + bob;
    this.halo.scale.setScalar(0.92 + glow * 0.12);
    this.groundRing.scale.setScalar(0.95 + glow * 0.1);

    for (const sparkle of this.sparkles) {
      const a = t * 1.2 + sparkle.userData.phase;
      sparkle.position.set(Math.cos(a) * 1.0, POTION_LIFT + bob + Math.sin(t * 2.3 + sparkle.userData.phase) * 0.5, Math.sin(a) * 1.0);
      sparkle.rotation.y = t * 3;
    }
  }

  remove() {
    if (this.mesh) {
      this.scene.remove(this.mesh);
      const geometries = new Set();
      this.mesh.traverse(child => {
        if (child.isMesh) geometries.add(child.geometry);
      });
      geometries.forEach(geometry => geometry.dispose());
      this.ownMaterials.forEach(material => material.dispose());
      this.ownMaterials = [];
    }
  }
}
